import type { FeatureCollection, Polygon } from 'geojson';

import { apiClient } from '~api/client';

type UnsurveyedQueryParams = {
  /** Name of the blast to query unsurveyed sites for */
  blast_name: string;
  /** Mine code to filter unsurveyed sites, optional */
  mine_name?: string;
  /** Buffer distance for the query */
  query_buffer?: number;
  /** Clip buffer distance */
  clip_buffer?: number;
};

type UnsurveyedSiteProperties = {
  name: string;
  is_valid: boolean;
  error_reason: string | null;
  error_location: string | null;
  distance: number | null;
  vibration_limit: number | null;
  flyrock_limit: number | null;
  overpressure_limit: number | null;
  dust_limit: number | null;
  ihmp_approved_impact_level: string | null;
  geotechnical_sensitivity: string | null;
  site_assessment: string | null;
};

type UnsurveyedResponse = FeatureCollection<Polygon, UnsurveyedSiteProperties> & {
  metadata: {
    blast_geojson: Polygon;
  };
};

/**
 * Unsurveyed sites service for accessing geometrically invalidated site data
 */
const unsurveyedService = {
  /**
   * Fetch unsurveyed sites for a specific blast
   * @param params - Query parameters including blast name and mine code
   * @returns Promise resolving to unsurveyed sites GeoJSON response
   */
  getUnsurveyedSites: async (params: UnsurveyedQueryParams): Promise<UnsurveyedResponse> => {
    const { blast_name, mine_name, clip_buffer, query_buffer = 350 } = params;
    const { data } = await apiClient.get(`/unsurveyed_sites/${encodeURIComponent(blast_name)}`, {
      params: { mine_name, query_buffer, clip_buffer, crs: 'local' },
    });
    return data;
  },
};

export type { UnsurveyedQueryParams, UnsurveyedResponse, UnsurveyedSiteProperties };
export { unsurveyedService };
